import { Card, CardContent, CardHeader } from "../../primitives/card";
import { Badge } from "../../primitives/badge";
import { Button } from "../../primitives/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../primitives/dropdown-menu";
import {
  GitBranch,
  MessageSquare,
  ExternalLink,
  User,
  Clock,
  MoreVertical,
  Trash2,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import type { FeatureBranch } from "@shared-core/data/feature-session-types";

interface FeatureCardProps {
  feature: FeatureBranch;
  onClick?: () => void;
  /** When provided, a three-dot menu offers Delete. The parent owns the
   *  confirmation + the adapter call so this stays presentational. */
  onDelete?: () => void;
}

export function FeatureCard({ feature, onClick, onDelete }: FeatureCardProps) {
  const isActive = !!feature.active_environment_id;
  const updatedDate = new Date(feature.updated_at);

  return (
    <Card
      className="cursor-pointer hover:shadow-md transition-shadow"
      onClick={onClick}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <GitBranch className="size-4 text-muted-foreground flex-shrink-0" />
            <span className="font-semibold text-sm truncate">
              {feature.branch_name}
            </span>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <Badge
              variant={isActive ? "default" : "secondary"}
              className={
                isActive ? "bg-green-100 text-green-800 border-green-200" : ""
              }
            >
              <span
                className={`inline-block size-1.5 rounded-full mr-1.5 ${
                  isActive ? "bg-green-500" : "bg-gray-400"
                }`}
              />
              {isActive ? "Active" : "Idle"}
            </Badge>
            {onDelete && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7 text-muted-foreground"
                    aria-label="Feature actions"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreVertical className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  onClick={(e) => e.stopPropagation()}
                >
                  <DropdownMenuItem
                    className="text-red-600 focus:text-red-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete();
                    }}
                  >
                    <Trash2 className="size-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <User className="size-3" />
            {feature.owner_user_id}
          </span>
          {feature.feedback_ids.length > 0 && (
            <span className="flex items-center gap-1">
              <MessageSquare className="size-3" />
              {feature.feedback_ids.length} feedback
            </span>
          )}
        </div>

        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Clock className="size-3" />
            Updated {formatDistanceToNow(updatedDate, { addSuffix: true })}
          </span>
          {feature.last_known_pr_url && (
            <a
              href={feature.last_known_pr_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-primary flex items-center gap-1 hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="size-3" />
              PR
            </a>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
